import React, { useEffect } from 'react';
import {
  Gavel,
  Briefcase,
  FileText,
  CreditCard,
  Copyright,
  AlertTriangle,
  CalendarX,
  Scale,
  RefreshCw,
} from 'lucide-react';
import SEO from '../components/SEO';
import PolicyPageLayout, { PolicySection } from '../components/legal/PolicyPageLayout';
import { setPageTitle } from '../utils/pageTitle';

const sections: PolicySection[] = [
  {
    title: 'Agreement Overview',
    icon: <Gavel size={16} />,
    content: 'These Terms and Conditions define the legal agreement between ConsultSRE and the client for consulting, implementation, and support services.',
  },
  {
    title: 'Service Scope',
    icon: <Briefcase size={16} />,
    content: 'Services are delivered according to signed proposals or statements of work.',
    bullets: [
      'Remote consulting sessions and technical workshops.',
      'Implementation, deployment, and optimization support.',
      'Documentation, knowledge transfer, and operational guidance.',
      'Optional ongoing maintenance and advisory support.',
    ],
  },
  {
    title: 'Client Responsibilities',
    icon: <FileText size={16} />,
    content: 'Clients are responsible for providing accurate inputs, required access, timely approvals, and internal coordination.',
  },
  {
    title: 'Fees and Payment',
    icon: <CreditCard size={16} />,
    content: 'Pricing model and invoicing cycle are defined in the applicable agreement. Where no specific terms are stated, payment is due within 30 days of invoice date. Late payments may pause service delivery until the outstanding balance is settled.',
  },
  {
    title: 'Intellectual Property',
    icon: <Copyright size={16} />,
    content: 'Ownership and licensing terms for deliverables are governed by the signed engagement terms, including any agreed transfer or usage rights.',
  },
  {
    title: 'Limitation of Liability',
    icon: <AlertTriangle size={16} />,
    content: 'ConsultSRE is not liable for third-party outages, client-side operational errors, or indirect/consequential losses except where required by law.',
  },
  {
    title: 'Termination',
    icon: <CalendarX size={16} />,
    content: 'Either party may terminate under the conditions in the service agreement, including notice periods and breach-related termination rights.',
  },
  {
    title: 'Governing Law',
    icon: <Scale size={16} />,
    content: 'Where a signed agreement specifies a governing jurisdiction, those terms apply. In the absence of a signed agreement, any dispute shall be resolved under the laws of the country in which the primary service operator is based, and the parties submit to the non-exclusive jurisdiction of the courts of that country.',
  },
  {
    title: 'Amendments',
    icon: <RefreshCw size={16} />,
    content: 'We may update these terms periodically with reasonable notice. Continued use of services after the effective date of an update implies acceptance of the revised terms. Changes do not apply retroactively to engagements already in progress without mutual written consent.',
  },
];

const TermsAndConditions = () => {
  useEffect(() => {
    setPageTitle('Terms and Conditions');
  }, []);

  return (
    <>
      <SEO
        title="Terms and Conditions | ConsultSRE"
        description="Terms and conditions for ConsultSRE's cloud infrastructure, DevOps, and SRE consulting solutions."
        type="website"
      />
      <PolicyPageLayout
        title="Terms and Conditions"
        description="The contractual terms governing use of our consulting services."
        effectiveDate="February 27, 2026"
        intro="These terms apply to all ConsultSRE service engagements unless superseded by a signed master agreement, statement of work, or order form."
        sections={sections}
      />
    </>
  );
};

export default TermsAndConditions;
